import { type Entries, InstantieId, type Projection } from '@eformulieren/model'
import type { LoginOption } from '@eformulieren/renderer'
import type * as Y from 'yjs'
import { loginOptions } from './login.js'

/**
 * Een sessie die verloopt terwijl het formulier openstaat (R-154, R-165, R-141, ontwerp 6.4 en 3.3).
 *
 * Ontwerp 6.4 zegt bij "Het token verloopt": lukt een stille vernieuwing niet, dan stopt het opslaan
 * met een melding en volgt opnieuw inloggen, in plaats van stil te falen. Een stille vernieuwing is er
 * niet (ontwerp 3.3, open punt), dus het token verloopt echt. De pod merkt dat op twee plekken:
 *
 *   - **De samenwerkverbinding** sluit hij met `token-verlopen` (4002, `CLOSE_REASONS` in
 *     `apps/runtime/src/collaboration.ts`), bij de eerste wijziging na de vervaltijd of in de
 *     periodieke ronde. De wijziging die de sluiting veroorzaakte neemt hij niet aan. De provider
 *     verbindt daarna opnieuw, zonder cookie (de browser gooit het weg op dezelfde vervaltijd), en
 *     dan weigert de handdruk zonder sluiting: de provider meldt `authenticationFailed`.
 *   - **Een verzoek** krijgt 401 met `geen-geldig-token`.
 *
 * Tot deze opdracht zag de invuller bij het eerste niets (de opslagindicator bleef op "Opslaan…"
 * staan, want de opslag wachtte op een bevestiging die niet meer kwam) en bij het tweede een melding
 * zonder weg naar het inloggen, die bovendien zei dat alles was bewaard. Nu staat er een melding met
 * een knop "Opnieuw inloggen" (`SessionExpired.tsx` in de renderer), en het formulier blijft in beeld,
 * zodat wat nog niet is opgeslagen er staat om over te nemen. Dezelfde regel als bij een verlaagd recht
 * in de tabel van 6.4.
 *
 * Dit bestand is `window`-vrij, zodat de beslissingen in Node te toetsen zijn (dezelfde keuze als
 * `login.ts`).
 */

/** De sluiting van de pod bij een verlopen token (`CLOSE_REASONS` in `collaboration.ts`). */
const TOKEN_EXPIRED = { code: 4002, reden: 'token-verlopen' } as const

/**
 * Of deze sluiting een verlopen sessie is.
 *
 * Twee vormen, net als bij `isEndOfAccess` in `access.ts`: sluit de pod de socket, dan komt de code
 * met de reden mee; een sluitbericht voor één document komt als code 1000 met de reden.
 */
export function isSessionExpiredClose(closing: { readonly code?: number; readonly reason?: string } | undefined): boolean {
  if (closing === undefined) return false
  return closing.code === TOKEN_EXPIRED.code || closing.reason === TOKEN_EXPIRED.reden
}

/**
 * Of dit antwoord van de pod een verlopen sessie is.
 *
 * Op de status en niet op de code in het lijf: de pod geeft 401 alleen als er geen geldig token is
 * (`server.ts`, `token.ts`), en een proxy die er een eigen lijf bij zet verandert daar niets aan.
 */
export function isSessionExpiredResponse(status: number): boolean {
  return status === 401
}

/** Een opslag of een sprong die niet kan doorgaan, omdat de sessie is verlopen. */
export class SessionExpiredError extends Error {
  constructor() {
    super('de sessie is verlopen; er wordt niets meer opgeslagen')
    this.name = 'SessionExpiredError'
  }
}

/**
 * Waar de inlogdienst na het opnieuw inloggen naartoe stuurt: de instantie die nu openstaat.
 *
 * **De instantie uit de stand en niet uit de adresbalk.** Een sprong in de reeks werkt de URL niet bij
 * (`onNavigate` in `FormPage`), dus de adresbalk kan een ander dossier noemen dan het scherm toont.
 * Of het pad een instantie is, toetst `InstantieId` uit het model, de enige plek waar die vorm staat.
 *
 * **Zonder query en zonder fragment.** Stond het formulier hier al, dan heeft de pagina de voorvulling
 * al naar de pod gestuurd; ze nog eens meesturen zou een veld dat de invuller met opzet heeft
 * leeggemaakt opnieuw vullen. Kwam het formulier nooit op, dan zet `FormPage` de voorvulling er zelf
 * weer bij, langs `targetKeepingPrefill` (`prefillStash.ts`). Het fragment weigert de inlogdienst.
 */
export function reloginTarget(location: { readonly origin: string; readonly pathname: string }, instantieId: string): string {
  // Zelfde ontleding als `instantieFromPath` in `FormPage`: kop- en staartslash eraf.
  const segment = location.pathname.replace(/^\/+|\/+$/g, '')
  // Staat er geen instantie in het pad (een proef op het wortelpad), dan blijft het pad wat het is.
  if (!InstantieId.safeParse(segment).success) return `${location.origin}${location.pathname}`
  return `${location.origin}/${encodeURIComponent(instantieId)}`
}

/**
 * De knoppen naar de inlogdienst: alleen de ingangen aan de kant van wie hier werkte.
 *
 * Wie als aanvrager werkte, logt als aanvrager opnieuw in; een knop "als medewerker van NWO" erbij
 * zou een keuze vragen die hij al heeft gemaakt. Is de kant niet bekend (de sessie verliep voordat de
 * projectie er was), of heeft de pod aan die kant geen ingang, dan alle ingangen. Noemt de pod zijn
 * ingangen niet, dan niets, en verwijst de melding naar deze pagina zelf: die vraagt bij het opkomen
 * `/whoami`, krijgt 401 en toont de gewone keuze (`main.tsx`).
 */
export function reloginOptions(
  entries: Entries | undefined,
  kant: 'intern' | 'extern' | undefined,
  target: string,
): readonly LoginOption[] {
  if (entries === undefined) return []
  const all = loginOptions(entries, target)
  const ownSide = kant === undefined ? [] : all.filter((option) => option.kant === kant)
  return ownSide.length > 0 ? ownSide : all
}

/**
 * De knoppen om aan de andere kant in te loggen, op de pagina "geen toegang" (besluit van de product owner
 * van 3 november 2026).
 *
 * Beide ingangen gaan naar dezelfde tenant, dus wie langs de verkeerde knop binnenkwam, is een andere
 * persoon (`nwo:<oid>` in plaats van `portaal:<oid>`) en krijgt 404 (R-155a). Deze knoppen staan bij elke
 * 404, ook als het formulier niet bestaat: dezelfde pagina voor beide, zodat ze niets verklappen. De eigen
 * ingang is het deel van `persoon` vóór de dubbele punt; de knoppen zijn de ingangen met een andere naam.
 * Is de eigen ingang niet te herkennen, dan alle ingangen. Noemt de pod zijn ingangen niet, dan niets.
 */
export function otherSideOptions(entries: Entries | undefined, persoon: string, target: string): readonly LoginOption[] {
  if (entries === undefined) return []
  const own = persoon.slice(0, Math.max(persoon.indexOf(':'), 0))
  const all = loginOptions(entries, target)
  const others = all.filter((option) => option.gastheer !== own)
  return others.length === all.length || others.length === 0 ? all : others
}

/** Wat de tracker van een provider gebruikt: het teltal van wat de pod nog niet heeft bevestigd. */
export interface UnsyncedSource {
  on(event: 'unsyncedChanges', listener: (data: { number: number }) => void): unknown
}

export interface UnsavedTracker {
  /** Volg dit sectiedocument, met de provider die het naar de pod brengt. */
  watch(doc: Y.Doc, provider: UnsyncedSource): void
  /** Of er een eigen wijziging is die de pod niet heeft bevestigd. */
  unsaved(): boolean
  /** De sessie is verlopen: vanaf nu bevestigt niemand meer iets. */
  freeze(): void
  /**
   * Volg dit document niet meer: de pagina gooide het weg bij een generatienaad (eisenboek 12.50). Wat er
   * onbevestigd in stond, zet de pagina in het nieuwe document terug, en dat telt daar.
   */
  forget(doc: Y.Doc): void
  /** Haakt los van alle documenten. */
  stop(): void
}

/**
 * Houdt per sectiedocument bij of er een eigen wijziging is die de pod nog niet heeft bevestigd.
 *
 * **Niet `hasUnsyncedChanges` van de provider.** Dat teltal zet `startSync` bij elke (her)verbinding
 * op 1, en na een geweigerde handdruk komt het nooit meer op 0: op dat getal alleen zou de melding
 * zeggen dat er iets verloren gaat terwijl er niets is getypt. Daarom zelf: een wijziging waarvan de
 * provider niet de herkomst is, is eigen invoer en zet het document op onbevestigd; het teltal op 0
 * zegt dat de pod alles heeft aangenomen wat de provider verstuurde, en zet het terug.
 *
 * **Na het verlopen bevestigt niemand meer.** Wat dan onbevestigd is blijft dat, en elke wijziging
 * daarna ook. Dat geldt ook voor de aanslag die de sluiting veroorzaakte: de pod nam haar niet aan.
 *
 * `onChange` hoort alleen een verandering van het totaal, zodat de pagina niet bij elke aanslag
 * opnieuw rendert.
 */
export function createUnsavedTracker(onChange?: (unsaved: boolean) => void): UnsavedTracker {
  const pending = new Set<Y.Doc>()
  const detach: (() => void)[] = []
  const detachOf = new Map<Y.Doc, () => void>()
  let frozen = false
  let last = false

  const report = (): void => {
    const now = pending.size > 0
    if (now === last) return
    last = now
    onChange?.(now)
  }

  return {
    watch(doc, provider) {
      const onUpdate = (_update: Uint8Array, origin: unknown): void => {
        if (origin === provider) return
        pending.add(doc)
        report()
      }
      doc.on('update', onUpdate)
      const off = (): void => doc.off('update', onUpdate)
      detach.push(off)
      detachOf.set(doc, off)
      provider.on('unsyncedChanges', ({ number }) => {
        if (frozen || number !== 0 || !detachOf.has(doc)) return
        pending.delete(doc)
        report()
      })
    },
    forget(doc) {
      detachOf.get(doc)?.()
      detachOf.delete(doc)
      pending.delete(doc)
      report()
    },
    unsaved: () => pending.size > 0,
    freeze() {
      frozen = true
    },
    stop() {
      for (const off of detach.splice(0)) off()
    },
  }
}

/**
 * De projectie na het verlopen: elke sectie alleen-lezen (ontwerp 6.4).
 *
 * Wat de invuller nu nog zou typen komt nergens meer aan, en een formulier dat bewerkbaar lijkt
 * terwijl het niet opslaat is precies wat 6.4 uitsluit ("in plaats van stil te falen"). Dezelfde vorm
 * als bij een verlaagd recht in die tabel: alleen-lezen, en wat er staat blijft in beeld.
 *
 * **Op de rechten en niet op de gedaante.** Een andere gedaante (`nalezen`) zou de schil vertellen
 * dat de beurt is gesloten en de actiebalk wegnemen; dat is niet wat er is gebeurd. Met `recht` op
 * `lezen` zetten de velden hun bewerkbaarheid op hun plek uit (`setEditable` in de rich-text-editor,
 * `readOnly` op een invoerveld), zonder ze opnieuw op te bouwen: dezelfde documenten, dezelfde
 * editors, en tekst blijft te selecteren en te kopiëren (R-166). De voorvulling slaat een sectie
 * zonder bewerkrecht over, dus die schrijft daarna ook niets meer.
 */
export function readOnlyAfterExpiry(projection: Projection): Projection {
  if (!projection.secties.some((section) => section.recht === 'bewerken')) return projection
  return {
    ...projection,
    secties: projection.secties.map((section) => (section.recht === 'bewerken' ? { ...section, recht: 'lezen' as const } : section)),
  }
}

/** De sleutel in de opslag van dit tabblad waar de plek van een verlenging wacht. */
const RETURN_PLACE_KEY = 'eformulieren:verlenging'
/** Hoe lang een bewaarde plek geldt: ruim een gang langs de identiteitsprovider, en niet langer. */
export const RETURN_PLACE_VALIDITY_MS = 10 * 60 * 1000

/** Wat de opslag van dit tabblad moet kunnen; `sessionStorage` past erin. */
export interface PlaceStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * Waar de invuller stond toen hij op "Sessie verlengen" drukte (WCAG 2.2.1, R-139).
 *
 * Een verlenging is een navigatie langs de inlogdienst en de identiteitsprovider (`renewOptions` in
 * `logout.ts`), en de pagina laadt daarna opnieuw. De invoer zelf staat dan in de opslag, want de
 * pagina navigeert pas als alles is bevestigd; wat er niet in de opslag staat is waar hij was. Het
 * doel naar de inlogdienst mag geen fragment dragen, dus de plek wacht in dit tabblad, bij de instantie
 * en met een tijdstempel, zoals een lange voorvulling (`prefillStash.ts`).
 */
export function stashReturnPlace(storage: PlaceStorage, instantieId: string, scrollY: number, now: number): void {
  try {
    storage.setItem(RETURN_PLACE_KEY, JSON.stringify({ instantieId, scrollY: Math.max(0, Math.round(scrollY)), at: now }))
  } catch {
    // Geen opslag: de verlenging werkt, alleen komt de pagina bovenaan terug.
  }
}

/** De bewaarde plek voor deze instantie, één keer gelezen en daarmee gewist; anders niets. */
export function takeReturnPlace(storage: PlaceStorage, instantieId: string, now: number): number | undefined {
  let raw: string | null
  try {
    raw = storage.getItem(RETURN_PLACE_KEY)
    storage.removeItem(RETURN_PLACE_KEY)
  } catch {
    return undefined
  }
  if (raw === null) return undefined
  try {
    const read = JSON.parse(raw) as { instantieId?: unknown; scrollY?: unknown; at?: unknown }
    if (read.instantieId !== instantieId || typeof read.scrollY !== 'number' || typeof read.at !== 'number') return undefined
    if (now - read.at > RETURN_PLACE_VALIDITY_MS || now < read.at) return undefined
    return read.scrollY
  } catch {
    return undefined
  }
}

/**
 * Wacht tot er niets meer onbevestigd is, of tot de tijd om is (`true` als alles is opgeslagen).
 *
 * Een verlenging laadt de pagina opnieuw, en wat de pod dan niet heeft is weg; daarom navigeert de
 * pagina pas als de tracker leeg is. Met een klok en een wachtfunctie van buiten, zodat het te toetsen
 * is zonder te wachten.
 */
export async function waitUntilSaved(
  unsaved: () => boolean,
  options: { readonly maxMs: number; readonly stepMs: number; readonly sleep?: (ms: number) => Promise<void> },
): Promise<boolean> {
  const sleep = options.sleep ?? (async (ms: number) => await new Promise<void>((done) => setTimeout(done, ms)))
  for (let waited = 0; unsaved(); waited += options.stepMs) {
    if (waited >= options.maxMs) return false
    await sleep(options.stepMs)
  }
  return true
}
